import uuidv4 from 'uuid/v4';

export const INSTALMENT_STATUSES = Object.freeze({
  PENDING: 'PENDING',
  APPROVED: 'APPROVED',
  INVOICED: 'INVOICED',
  PAID: 'PAID',
  CANCELLED: 'CANCELLED',
});

const INSTALMENT_STATUS_TIMESTAMP_MAP = Object.freeze({
  [INSTALMENT_STATUSES.APPROVED]: 'approvedAt',
  [INSTALMENT_STATUSES.INVOICED]: 'invoicedAt',
  [INSTALMENT_STATUSES.PAID]: 'paidAt',
  [INSTALMENT_STATUSES.CANCELLED]: 'cancelledAt',
});

const toCents = (value) => Math.round(Number(value || 0) * 100);

export const createDefaultInstalment = (sum = 0) => ({
  id: uuidv4(),
  description: '',
  sum: Number(sum) || 0,
  locked: false,
  status: INSTALMENT_STATUSES.PENDING,
  dueDate: null,
  createdAt: new Date().toISOString(),
});

// Cent-precise even split across unlocked instalments.
// Last unlocked absorbs the rounding remainder so Σ instalments === totalSum (to the cent),
// as long as no locked sums make the remainder negative.
export const redistributeInstalments = (instalments, totalSum) => {
  if (!Array.isArray(instalments) || instalments.length === 0) {
    return instalments;
  }

  const unlocked = instalments.filter((i) => !i.locked);
  if (unlocked.length === 0) {
    return instalments;
  }

  const lockedCents = instalments
    .filter((i) => i.locked)
    .reduce((acc, i) => acc + toCents(i.sum), 0);

  const remainingCents = toCents(totalSum) - lockedCents;
  const baseCents = Math.trunc(remainingCents / unlocked.length);
  const remainderCents = remainingCents - baseCents * unlocked.length;
  const lastUnlockedId = unlocked[unlocked.length - 1].id;

  return instalments.map((i) => {
    if (i.locked) return i;
    const cents = i.id === lastUnlockedId ? baseCents + remainderCents : baseCents;
    return { ...i, sum: cents / 100 };
  });
};

export const addInstalment = (instalments, totalSum) => {
  const next = [...(instalments || []), createDefaultInstalment(0)];
  return redistributeInstalments(next, totalSum);
};

const countUnlocked = (instalments) =>
  (instalments || []).filter((i) => !i.locked).length;

export const canDeleteInstalment = (instalments, id) => {
  if (!Array.isArray(instalments) || instalments.length <= 1) return false;
  const target = instalments.find((i) => i.id === id);
  if (!target) return false;
  // Removing the last unlocked instalment while locked siblings remain
  // would violate the "≥1 unlocked" invariant.
  if (!target.locked && countUnlocked(instalments) === 1) return false;
  return true;
};

export const removeInstalment = (instalments, id, totalSum) => {
  if (!canDeleteInstalment(instalments, id)) return instalments;
  const next = instalments.filter((i) => i.id !== id);
  return redistributeInstalments(next, totalSum);
};

export const canLockInstalment = (instalments, id) => {
  const target = (instalments || []).find((i) => i.id === id);
  if (!target) return false;
  if (target.locked) return true; // unlocking is always allowed
  // Locking the last unlocked instalment would leave none unlocked.
  return countUnlocked(instalments) > 1;
};

export const toggleInstalmentLock = (instalments, id, totalSum) => {
  const target = (instalments || []).find((i) => i.id === id);
  if (!target) return instalments;
  if (!canLockInstalment(instalments, id)) return instalments;

  const wasLocked = target.locked;
  const next = instalments.map((i) =>
    i.id === id ? { ...i, locked: !i.locked } : i
  );

  // If we just unlocked, the freshly-unlocked instalment rejoins the split pool.
  // If we just locked, no redistribution is needed — its sum is whatever it was.
  return wasLocked ? redistributeInstalments(next, totalSum) : next;
};

export const setInstalmentField = (instalments, id, field, value, totalSum) => {
  if (!Array.isArray(instalments)) return instalments;

  if (field === 'sum') {
    // Editing the sum auto-locks this instalment, then unlocked siblings re-split.
    const updated = instalments.map((i) =>
      i.id === id ? { ...i, sum: Number(value) || 0, locked: true } : i
    );
    return redistributeInstalments(updated, totalSum);
  }

  if (field === 'status') {
    return setInstalmentStatus(instalments, id, value);
  }

  return instalments.map((i) => (i.id === id ? { ...i, [field]: value } : i));
};

export const setInstalmentStatus = (instalments, id, status) => {
  if (!Array.isArray(instalments)) return instalments;
  const timestampField = INSTALMENT_STATUS_TIMESTAMP_MAP[status];
  const nowIso = new Date().toISOString();

  return instalments.map((i) => {
    if (i.id !== id) return i;
    if (i.status === status) return i;
    const next = { ...i, status, updatedAt: nowIso };
    if (timestampField) {
      next[timestampField] = nowIso;
    }
    return next;
  });
};

// Returns { ok, message } describing any non-blocking warning to surface.
export const validateInstalments = (instalments, totalSum) => {
  if (!Array.isArray(instalments) || instalments.length === 0) {
    return { ok: true, message: '' };
  }

  const negativeUnlocked = instalments.find((i) => !i.locked && Number(i.sum) < 0);
  if (negativeUnlocked) {
    return {
      ok: false,
      message:
        'Lukittujen erien summa ylittää kokonaissumman. Vapauta tai pienennä jotain lukittua erää.',
    };
  }

  const sumCents = instalments.reduce((acc, i) => acc + toCents(i.sum), 0);
  const diffCents = sumCents - toCents(totalSum);
  if (Math.abs(diffCents) > 1) {
    return {
      ok: false,
      message: `Maksuerien summa ei vastaa kokonaissummaa (ero ${
        diffCents / 100
      } €).`,
    };
  }

  return { ok: true, message: '' };
};
